import { formatISODate, utcTimeToLocal } from '@common/utils/dateUtils';
import { ActionButton } from '@common/components';
import {
  EditIcon,
  ModifyReportCriteriaIcon,
  EnableIcon,
  DisableIcon,
  Delete2Icon,
} from '@common/components/icons';
import { ActionsCell } from '@common/components/Table/Cells';
import { ListTooltip } from '@common/components/Table/Tooltips';
import EditableStatusDropdown from '@common/components/EditableStatusDropdown/EditableStatusDropdown';
import { SavedReportsCell, toReportNamesArray } from './cells';
import * as styles from './styles.module.css';

/**
 * Format schedule type and time for display
 * Converts UTC time from server to user's local timezone
 */
const formatSchedule = (scheduleType, scheduleTime, t) => {
  const typeLabel = t(`scheduledReports.schedule.${scheduleType}`) || scheduleType;
  const localTime = utcTimeToLocal(scheduleTime);
  return `${typeLabel} @ ${localTime}`;
};

/**
 * Create table columns for scheduled reports
 */
export const createColumns = (t, { onEditNameFormat, onModifyScheduledReport, onEnable, onDisable, onDelete, onUpdateStatus }) => {
  const actionsMenu = [
    {
      key: 'editNameFormat',
      component: (
        <ActionButton
          icon={EditIcon}
          text={t('scheduledReports.actions.editNameFormat')}
          className={styles['action-button']}
        />
      ),
      onClick: (row) => {
        onEditNameFormat?.(row.original);
      },
    },
    {
      key: 'modifyScheduledReport',
      component: (
        <ActionButton
          icon={ModifyReportCriteriaIcon}
          text={t('scheduledReports.actions.modifyScheduledReport')}
          className={styles['action-button']}
        />
      ),
      onClick: (row) => {
        onModifyScheduledReport?.(row.original);
      },
    },
    {
      key: 'enable',
      component: (
        <ActionButton
          icon={EnableIcon}
          text={t('common.enable')}
          className={styles['action-button']}
        />
      ),
      onClick: (row) => {
        onEnable?.(row.original);
      },
      hidden: (row) => row.original.status === 1, // Hide if already enabled
    },
    {
      key: 'disable',
      component: (
        <ActionButton
          icon={DisableIcon}
          text={t('common.disable')}
          className={styles['action-button']}
        />
      ),
      onClick: (row) => {
        onDisable?.(row.original);
      },
      hidden: (row) => row.original.status === 0, // Hide if already disabled
    },
    {
      key: 'delete',
      component: (
        <ActionButton
          icon={Delete2Icon}
          text={t('scheduledReports.actions.delete')}
          className={`${styles['action-button']} ${styles['delete-action']}`}
        />
      ),
      onClick: (row) => {
        onDelete?.(row.original);
      },
    },
  ];

  return [
    {
      id: 'select',
      staticWidth: 37,
      enableResizing: false,
      enableSorting: false,
      meta: { label: 'Selection' },
    },
    {
      accessorKey: 'name',
      header: t('common.name'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 200,
      cell: ({ getValue }) => getValue() || '-',
    },
    {
      accessorKey: 'description',
      header: t('common.description'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 200,
      cell: ({ getValue }) => getValue() || '-',
    },
    {
      accessorKey: 'savedReports',
      header: t('scheduledReports.columns.SavedReports'),
      enableColumnFilter: false,
      enableColumnResizing: true,
      enableSorting: false,
      minWidth: 150,
      tooltip: ({ row }) => {
        const names = toReportNamesArray(row.original.savedReports);
        return names.length > 0 ? <ListTooltip items={names} /> : null;
      },
      cell: ({ getValue }) => <SavedReportsCell savedReports={getValue()} />,
    },
    {
      accessorKey: 'scheduleType',
      header: t('scheduledReports.columns.scheduleType'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 150,
      cell: ({ row }) => formatSchedule(row.original.scheduleType, row.original.scheduleTime, t),
    },
    {
      accessorKey: 'outputFormat',
      header: t('scheduledReports.columns.outputFormat'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 100,
      cell: ({ getValue }) => {
        const format = getValue();
        return format?.toUpperCase() || '-';
      },
    },
    {
      accessorKey: 'status',
      header: t('scheduledReports.columns.status'),
      enableColumnFilter: true,
      enableColumnResizing: false,
      staticWidth: 130,
      cell: ({ getValue, row }) => {
        const scheduledReport = row.original;
        return (
          <EditableStatusDropdown
            value={getValue()}
            onUpdate={(newValue) => onUpdateStatus?.(scheduledReport, newValue)}
            showStatusDot
          />
        );
      },
    },
    {
      accessorKey: 'nextRun',
      header: t('scheduledReports.columns.nextRun'),
      enableColumnFilter: false,
      enableColumnResizing: true,
      minWidth: 150,
      cell: ({ getValue }) => formatISODate(getValue()),
    },
    {
      accessorKey: 'createdAt',
      header: t('scheduledReports.columns.createdAt'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 150,
      cell: ({ getValue }) => formatISODate(getValue()),
    },
    {
      id: 'actions',
      staticWidth: 27,
      cell: ({ row }) => <ActionsCell row={row} actionsMenu={actionsMenu} />,
      enableResizing: false,
      enableSorting: false,
      meta: { label: 'Actions' },
    },
  ];
};

